import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Task,
  UndistributedIncome,
  assignUndistributedIncomes,
  getPaymentDistributionMeta,
  getTasks,
  getUndistributedIncomes,
  syncUndistributedIncomes,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  SortState,
  compareValues,
  formatContractSumDisplay,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import '../styles/DataPage.css';
import './PaymentRequestPage.css';
import './PaymentDistributionPage.css';

type SortKey =
  | 'operation_date'
  | 'account_name'
  | 'category_name'
  | 'amount'
  | 'contractor_name_pf'
  | 'comment';

type FilterMenuKey = 'account' | 'category' | 'contractor' | null;

interface AssignmentPart {
  key: string;
  taskId: string;
  contractId: number | null;
  planfactArticle: string;
  amount: string;
}

interface AssignmentDraft {
  parts: AssignmentPart[];
}

interface TaskOption {
  taskId: string;
  shortName: string;
  contractIds: number[];
}

interface ContractOption {
  contractId: number;
  contractNumber: string;
  dealCode: string;
  taskIds: string[];
}

let partKeySeq = 0;
function nextPartKey(): string {
  partKeySeq += 1;
  return `part-${partKeySeq}`;
}

function formatContractDealCode(contractId: number): string {
  return `CTR${contractId}`;
}

function formatAmountInput(value: number): string {
  if (!Number.isFinite(value)) return '';
  return String(Math.round(value * 100) / 100);
}

function parseAmountBound(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string | null | undefined, from: string, to: string): boolean {
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) return true;
  if (!value?.trim()) return false;

  const ts = parseFilterDay(value.slice(0, 10));
  if (ts === null) return true;

  const fromTs = hasFrom ? parseFilterDay(from) ?? Number.NEGATIVE_INFINITY : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? parseFilterDay(to) ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

function displayValue(value: string | null | undefined): string {
  return value?.trim() ? value : '—';
}

function amountsMatch(left: number, right: number): boolean {
  return Math.abs(left - right) <= 0.01;
}

function buildTaskOptions(tasks: Task[]): TaskOption[] {
  const byId = new Map<string, TaskOption>();
  for (const task of tasks) {
    const taskId = task.task_id?.trim();
    if (!taskId) continue;
    const existing = byId.get(taskId);
    if (existing) {
      if (!existing.contractIds.includes(task.contract_id)) {
        existing.contractIds.push(task.contract_id);
      }
      continue;
    }
    byId.set(taskId, {
      taskId,
      shortName: task.short_name?.trim() || taskId,
      contractIds: [task.contract_id],
    });
  }
  return [...byId.values()].sort((a, b) => a.shortName.localeCompare(b.shortName, 'ru'));
}

function buildContractOptions(tasks: Task[]): ContractOption[] {
  const byId = new Map<number, ContractOption>();
  for (const task of tasks) {
    const taskId = task.task_id?.trim();
    if (!taskId) continue;
    const existing = byId.get(task.contract_id);
    if (existing) {
      if (!existing.taskIds.includes(taskId)) {
        existing.taskIds.push(taskId);
      }
      continue;
    }
    byId.set(task.contract_id, {
      contractId: task.contract_id,
      contractNumber: task.contract_number?.trim() || String(task.contract_id),
      dealCode: formatContractDealCode(task.contract_id),
      taskIds: [taskId],
    });
  }
  return [...byId.values()].sort((a, b) =>
    a.contractNumber.localeCompare(b.contractNumber, 'ru', { numeric: true }),
  );
}

function createDefaultPart(
  row: UndistributedIncome,
  articleOptions: string[],
  keySuffix = '0',
): AssignmentPart {
  const category = row.category_name?.trim() || '';
  const categoryKey = category.toLowerCase();
  const article =
    category
      ? articleOptions.find((item) => item.toLowerCase() === categoryKey) || ''
      : '';
  return {
    key: `${row.planfact_operation_id}-${keySuffix}`,
    taskId: '',
    contractId: null,
    planfactArticle: article,
    amount: formatAmountInput(Number(row.amount || 0)),
  };
}

function createEmptyPart(pfId: string): AssignmentPart {
  return {
    key: `${pfId}-${nextPartKey()}`,
    taskId: '',
    contractId: null,
    planfactArticle: '',
    amount: '',
  };
}

function buildInitialDrafts(
  incomeRows: UndistributedIncome[],
  articles: string[],
): Record<string, AssignmentDraft> {
  const next: Record<string, AssignmentDraft> = {};
  for (const row of incomeRows) {
    next[row.planfact_operation_id] = {
      parts: [createDefaultPart(row, articles)],
    };
  }
  return next;
}

const PaymentDistributionPage: React.FC = () => {
  const { can } = useAuth();
  const canView = can('INCOME', 'EDIT');
  const canEdit = can('INCOME', 'EDIT');

  const [rows, setRows] = useState<UndistributedIncome[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [articleOptions, setArticleOptions] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, AssignmentDraft>>({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<FilterMenuKey>(null);
  const [searchText, setSearchText] = useState('');
  const [filterAccounts, setFilterAccounts] = useState<string[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterContractors, setFilterContractors] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');

  const [sortState, setSortState] = useState<SortState<SortKey>>({
    key: 'operation_date',
    direction: 'desc',
  });

  const taskOptions = useMemo(() => buildTaskOptions(tasks), [tasks]);
  const contractOptions = useMemo(() => buildContractOptions(tasks), [tasks]);
  const articleSelectOptions = useMemo(
    () => articleOptions.map((article) => ({ value: article, label: article })),
    [articleOptions],
  );

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canView) {
      setLoading(false);
      setRows([]);
      setTasks([]);
      setArticleOptions([]);
      setDrafts({});
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [incomeRows, tasksData, meta] = await Promise.all([
        getUndistributedIncomes(),
        getTasks(),
        getPaymentDistributionMeta(),
      ]);
      const articles = meta.planfact_articles ?? [];
      setRows(incomeRows);
      setTasks(tasksData);
      setArticleOptions(articles);
      setDrafts(buildInitialDrafts(incomeRows, articles));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      setRows([]);
      setTasks([]);
      setArticleOptions([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const uniqueValues = useCallback(
    (picker: (row: UndistributedIncome) => string | undefined): string[] => {
      const set = new Set<string>();
      for (const row of rows) {
        const value = picker(row)?.trim();
        if (value) set.add(value);
      }
      return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
    },
    [rows],
  );

  const accountOptions = useMemo(() => uniqueValues((row) => row.account_name), [uniqueValues]);
  const categoryOptions = useMemo(() => uniqueValues((row) => row.category_name), [uniqueValues]);
  const contractorOptions = useMemo(
    () => uniqueValues((row) => row.contractor_name_pf),
    [uniqueValues],
  );

  const hasActiveFilters = useMemo(
    () =>
      searchText.trim() !== ''
      || filterAccounts.length > 0
      || filterCategories.length > 0
      || filterContractors.length > 0
      || filterDateFrom.trim() !== ''
      || filterDateTo.trim() !== ''
      || minAmountFilter.trim() !== ''
      || maxAmountFilter.trim() !== '',
    [
      searchText,
      filterAccounts,
      filterCategories,
      filterContractors,
      filterDateFrom,
      filterDateTo,
      minAmountFilter,
      maxAmountFilter,
    ],
  );

  const clearFilters = (): void => {
    setSearchText('');
    setFilterAccounts([]);
    setFilterCategories([]);
    setFilterContractors([]);
    setFilterDateFrom('');
    setFilterDateTo('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
  };

  const getDraft = useCallback(
    (row: UndistributedIncome): AssignmentDraft => {
      const existing = drafts[row.planfact_operation_id];
      if (existing) return existing;
      return { parts: [createDefaultPart(row, articleOptions)] };
    },
    [drafts, articleOptions],
  );

  const setDraftParts = (pfId: string, parts: AssignmentPart[]): void => {
    setDrafts((prev) => ({
      ...prev,
      [pfId]: { parts },
    }));
  };

  const updatePart = (
    row: UndistributedIncome,
    partKey: string,
    patch: Partial<AssignmentPart>,
  ): void => {
    const draft = getDraft(row);
    setDraftParts(
      row.planfact_operation_id,
      draft.parts.map((part) => (part.key === partKey ? { ...part, ...patch } : part)),
    );
  };

  const taskSelectOptionsForPart = (part: AssignmentPart) => {
    const list =
      part.contractId == null
        ? taskOptions
        : taskOptions.filter((option) => option.contractIds.includes(part.contractId as number));
    return list.map((option) => ({
      value: option.taskId,
      label: option.shortName,
    }));
  };

  const contractSelectOptionsForPart = (part: AssignmentPart) => {
    const list = !part.taskId.trim()
      ? contractOptions
      : contractOptions.filter((option) => option.taskIds.includes(part.taskId));
    return list.map((option) => ({
      value: String(option.contractId),
      label: `${option.contractNumber} (${option.dealCode})`,
    }));
  };

  const filteredRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    const minAmount = parseAmountBound(minAmountFilter);
    const maxAmount = parseAmountBound(maxAmountFilter);

    return rows.filter((row) => {
      if (needle) {
        const haystack = [
          row.account_name,
          row.category_name,
          row.contractor_name_pf,
          row.comment,
          row.deal_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (filterAccounts.length > 0 && !(row.account_name && filterAccounts.includes(row.account_name))) {
        return false;
      }
      if (filterCategories.length > 0 && !(row.category_name && filterCategories.includes(row.category_name))) {
        return false;
      }
      if (
        filterContractors.length > 0
        && !(row.contractor_name_pf && filterContractors.includes(row.contractor_name_pf))
      ) {
        return false;
      }
      if (!dateInRange(row.operation_date, filterDateFrom, filterDateTo)) {
        return false;
      }
      if (minAmount != null && Number(row.amount) < minAmount) return false;
      if (maxAmount != null && Number(row.amount) > maxAmount) return false;
      return true;
    });
  }, [
    rows,
    searchText,
    filterAccounts,
    filterCategories,
    filterContractors,
    filterDateFrom,
    filterDateTo,
    minAmountFilter,
    maxAmountFilter,
  ]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((left, right) => {
      switch (sortState.key) {
        case 'amount':
          return compareValues(Number(left.amount), Number(right.amount), sortState.direction);
        case 'operation_date':
          return compareValues(left.operation_date, right.operation_date, sortState.direction);
        case 'account_name':
          return compareValues(left.account_name || '', right.account_name || '', sortState.direction);
        case 'category_name':
          return compareValues(left.category_name || '', right.category_name || '', sortState.direction);
        case 'contractor_name_pf':
          return compareValues(
            left.contractor_name_pf || '',
            right.contractor_name_pf || '',
            sortState.direction,
          );
        case 'comment':
          return compareValues(left.comment || '', right.comment || '', sortState.direction);
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredRows, sortState]);

  const totalAmount = useMemo(
    () => sortedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [sortedRows],
  );

  const readyItems = useMemo(() => {
    const items: {
      planfact_operation_id: string;
      parts: {
        task_id: string;
        contract_id: number;
        planfact_article: string;
        amount: number;
      }[];
    }[] = [];

    for (const row of sortedRows) {
      const draft = drafts[row.planfact_operation_id] ?? getDraft(row);
      const parts = draft.parts;
      let partsSum = 0;
      const normalized: {
        task_id: string;
        contract_id: number;
        planfact_article: string;
        amount: number;
      }[] = [];

      let valid = true;
      for (const part of parts) {
        const amount = parseAmountBound(part.amount);
        if (
          !part.taskId.trim()
          || part.contractId == null
          || !part.planfactArticle.trim()
          || amount == null
          || amount <= 0
        ) {
          valid = false;
          break;
        }
        partsSum += amount;
        normalized.push({
          task_id: part.taskId.trim(),
          contract_id: part.contractId,
          planfact_article: part.planfactArticle.trim(),
          amount,
        });
      }
      if (!valid || !amountsMatch(partsSum, Number(row.amount || 0))) continue;
      items.push({
        planfact_operation_id: row.planfact_operation_id,
        parts: normalized,
      });
    }
    return items;
  }, [sortedRows, drafts, getDraft]);

  const handleSort = (key: SortKey): void => {
    setSortState((prev) => toggleSort(prev, key));
  };

  const renderSortableHeader = (key: SortKey, label: string, className = ''): React.ReactNode => (
    <th
      className={`sortable ${className}`.trim()}
      onClick={() => handleSort(key)}
      role="columnheader"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSort(key);
        }
      }}
      aria-sort={
        sortState.key === key ? (sortState.direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      {label}
      {sortState.key === key ? (sortState.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  const handleSync = async (): Promise<void> => {
    if (!canEdit || syncing) return;
    setSyncing(true);
    setError('');
    setSuccess(null);
    try {
      const result = await syncUndistributedIncomes();
      setSuccess(result.message);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка синхронизации');
    } finally {
      setSyncing(false);
    }
  };

  const handleAssign = async (): Promise<void> => {
    if (!canEdit || saving || readyItems.length === 0) return;
    setSaving(true);
    setError('');
    setSuccess(null);
    try {
      const result = await assignUndistributedIncomes(readyItems);
      if (result.errors.length > 0) {
        setError(result.errors.join('\n'));
      }
      if (result.assigned_count > 0) {
        setSuccess(`Отправлено в ПланФакт: ${result.assigned_count}`);
        await fetchData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка отправки');
    } finally {
      setSaving(false);
    }
  };

  const handleTaskChange = (
    row: UndistributedIncome,
    partKey: string,
    taskId: string,
  ): void => {
    const draft = getDraft(row);
    const part = draft.parts.find((item) => item.key === partKey);
    if (!part) return;
    let nextContractId = part.contractId;
    if (taskId && nextContractId != null) {
      const task = taskOptions.find((option) => option.taskId === taskId);
      if (task && !task.contractIds.includes(nextContractId)) {
        nextContractId = task.contractIds.length === 1 ? task.contractIds[0] : null;
      }
    } else if (taskId) {
      const task = taskOptions.find((option) => option.taskId === taskId);
      if (task && task.contractIds.length === 1) {
        nextContractId = task.contractIds[0];
      }
    }
    updatePart(row, partKey, { taskId, contractId: nextContractId });
  };

  const handleContractChange = (
    row: UndistributedIncome,
    partKey: string,
    contractIdRaw: string,
  ): void => {
    const draft = getDraft(row);
    const part = draft.parts.find((item) => item.key === partKey);
    if (!part) return;
    const contractId = contractIdRaw ? Number(contractIdRaw) : null;
    let nextTaskId = part.taskId;
    if (contractId != null && nextTaskId) {
      const contract = contractOptions.find((option) => option.contractId === contractId);
      if (contract && !contract.taskIds.includes(nextTaskId)) {
        nextTaskId = contract.taskIds.length === 1 ? contract.taskIds[0] : '';
      }
    } else if (contractId != null) {
      const contract = contractOptions.find((option) => option.contractId === contractId);
      if (contract && contract.taskIds.length === 1) {
        nextTaskId = contract.taskIds[0];
      }
    }
    updatePart(row, partKey, { taskId: nextTaskId, contractId });
  };

  const handleSplit = (row: UndistributedIncome): void => {
    const draft = getDraft(row);
    setDraftParts(row.planfact_operation_id, [...draft.parts, createEmptyPart(row.planfact_operation_id)]);
  };

  const handleRemovePart = (row: UndistributedIncome, partKey: string): void => {
    const draft = getDraft(row);
    if (draft.parts.length <= 1) return;
    const remaining = draft.parts.filter((part) => part.key !== partKey);
    if (remaining.length === 1) {
      setDraftParts(row.planfact_operation_id, [
        {
          ...remaining[0],
          amount: formatAmountInput(Number(row.amount || 0)),
        },
      ]);
      return;
    }
    setDraftParts(row.planfact_operation_id, remaining);
  };

  const partsSumMismatch = (row: UndistributedIncome, parts: AssignmentPart[]): boolean => {
    if (parts.length <= 1) return false;
    let sum = 0;
    let hasValue = false;
    for (const part of parts) {
      const amount = parseAmountBound(part.amount);
      if (amount == null) continue;
      hasValue = true;
      sum += amount;
    }
    if (!hasValue) return false;
    return !amountsMatch(sum, Number(row.amount || 0));
  };

  const colgroup = (
    <colgroup>
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--date" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--account" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--contractor" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--comment" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--contract" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--task-id" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--task" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--category" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--actions" />
      <col className="payment-distribution-page__colgrid payment-distribution-page__colgrid--amount" />
    </colgroup>
  );

  return (
    <div className="data-page tasks-page payment-distribution-page">
      {!canView ? (
        <div className="empty-state">Нет доступа (требуется INCOME EDIT).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Распределение платежей</h1>
            {canEdit ? (
              <div className="operations-page__sync-actions">
                <button
                  type="button"
                  className="create-button operations-page__sync-button"
                  onClick={() => void handleAssign()}
                  disabled={saving || syncing || readyItems.length === 0}
                >
                  {saving ? 'Отправка…' : 'Отправить в ПланФакт'}
                </button>
                <button
                  type="button"
                  className="filters-toggle operations-page__sync-button"
                  onClick={() => void handleSync()}
                  disabled={syncing || saving}
                >
                  {syncing ? 'Синхронизация…' : 'Синхр. из ПланФакт'}
                </button>
              </div>
            ) : null}
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message" style={{ whiteSpace: 'pre-wrap' }}>{error}</div> : null}
          {success ? <div className="success-message">{success}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen ? (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Счёт"
                      options={accountOptions}
                      selected={filterAccounts}
                      onSelectedChange={setFilterAccounts}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'account'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'account' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья"
                      options={categoryOptions}
                      selected={filterCategories}
                      onSelectedChange={setFilterCategories}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'category'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'category' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Контрагент"
                      options={contractorOptions}
                      selected={filterContractors}
                      onSelectedChange={setFilterContractors}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'contractor'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contractor' : null)}
                    />
                  </div>
                  <label>
                    Дата с
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата по
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                  </label>
                  <label>
                    Сумма от
                    <input value={minAmountFilter} onChange={(e) => setMinAmountFilter(e.target.value)} />
                  </label>
                  <label>
                    Сумма до
                    <input value={maxAmountFilter} onChange={(e) => setMaxAmountFilter(e.target.value)} />
                  </label>
                </div>
              </aside>
            ) : null}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table payment-distribution-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          {renderSortableHeader('operation_date', 'Дата')}
                          {renderSortableHeader('account_name', 'Счёт')}
                          {renderSortableHeader('contractor_name_pf', 'Контрагент')}
                          {renderSortableHeader('comment', 'Комментарий')}
                          <th>Договор</th>
                          <th>ID задачи</th>
                          <th>Задача</th>
                          {renderSortableHeader('category_name', 'Статья')}
                          <th aria-label="Действия" />
                          {renderSortableHeader('amount', 'Сумма', 'col-num')}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="empty-state">
                              Нет поступлений для распределения. Нажмите «Синхр. из ПланФакт».
                            </td>
                          </tr>
                        ) : (
                          sortedRows.map((row) => {
                            const draft = getDraft(row);
                            const parts = draft.parts;
                            const isSplit = parts.length > 1;
                            const mismatch = partsSumMismatch(row, parts);
                            return parts.map((part, partIndex) => (
                              <tr
                                key={`${row.planfact_operation_id}-${part.key}`}
                                className={
                                  partIndex > 0
                                    ? 'payment-distribution-page__split-row'
                                    : undefined
                                }
                              >
                                {partIndex === 0 ? (
                                  <>
                                    <td rowSpan={parts.length}>{formatDateShort(row.operation_date)}</td>
                                    <td rowSpan={parts.length}>
                                      <div className="payment-distribution-page__clamp-5">
                                        {displayValue(row.account_name)}
                                      </div>
                                    </td>
                                    <td rowSpan={parts.length}>
                                      <div className="payment-distribution-page__clamp-5">
                                        {displayValue(row.contractor_name_pf)}
                                      </div>
                                    </td>
                                    <td rowSpan={parts.length}>
                                      <div className="payment-distribution-page__clamp-5">
                                        {displayValue(row.comment)}
                                      </div>
                                    </td>
                                  </>
                                ) : null}
                                <td className="payment-distribution-page__td-picker">
                                  <SearchableSelect
                                    className="payment-request-picker payment-distribution-page__picker"
                                    options={contractSelectOptionsForPart(part)}
                                    value={part.contractId != null ? String(part.contractId) : ''}
                                    onChange={(value) => handleContractChange(row, part.key, value)}
                                    disabled={!canEdit || saving || contractOptions.length === 0}
                                    placeholder="Поиск договора…"
                                    emptyLabel="— выберите —"
                                    ariaLabel="Договор"
                                  />
                                </td>
                                <td className="payment-distribution-page__task-id">
                                  {part.taskId.trim() || '—'}
                                </td>
                                <td className="payment-distribution-page__td-picker">
                                  <SearchableSelect
                                    className="payment-request-picker payment-distribution-page__picker"
                                    options={taskSelectOptionsForPart(part)}
                                    value={part.taskId}
                                    onChange={(value) => handleTaskChange(row, part.key, value)}
                                    disabled={!canEdit || saving || taskOptions.length === 0}
                                    placeholder="Поиск задачи…"
                                    emptyLabel="— выберите —"
                                    ariaLabel="Задача"
                                  />
                                </td>
                                <td className="payment-distribution-page__td-picker">
                                  <SearchableSelect
                                    className="payment-request-picker payment-distribution-page__picker"
                                    options={articleSelectOptions}
                                    value={part.planfactArticle}
                                    onChange={(value) =>
                                      updatePart(row, part.key, { planfactArticle: value })
                                    }
                                    disabled={!canEdit || saving || articleSelectOptions.length === 0}
                                    placeholder="Поиск статьи…"
                                    emptyLabel="— выберите —"
                                    ariaLabel="Статья"
                                  />
                                </td>
                                <td className="payment-distribution-page__td-actions">
                                  {canEdit ? (
                                    <div className="payment-distribution-page__actions">
                                      <button
                                        type="button"
                                        className="payment-distribution-page__action-btn"
                                        onClick={() => handleSplit(row)}
                                        disabled={saving}
                                        title="Разбить сумму"
                                        aria-label="Разбить сумму"
                                      >
                                        +
                                      </button>
                                      {isSplit ? (
                                        <button
                                          type="button"
                                          className="payment-distribution-page__action-btn"
                                          onClick={() => handleRemovePart(row, part.key)}
                                          disabled={saving}
                                          title="Удалить часть"
                                          aria-label="Удалить часть"
                                        >
                                          −
                                        </button>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="col-num payment-distribution-page__td-amount">
                                  {canEdit && isSplit ? (
                                    <input
                                      type="text"
                                      className={`payment-distribution-page__amount-input${
                                        mismatch ? ' payment-distribution-page__amount-input--error' : ''
                                      }`}
                                      value={part.amount}
                                      onChange={(e) =>
                                        updatePart(row, part.key, { amount: e.target.value })
                                      }
                                      disabled={saving}
                                      inputMode="decimal"
                                      aria-label="Сумма"
                                    />
                                  ) : (
                                    formatContractSumDisplay(
                                      isSplit
                                        ? (parseAmountBound(part.amount) ?? 0)
                                        : Number(row.amount || 0),
                                    )
                                  )}
                                </td>
                              </tr>
                            ));
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer payment-distribution-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          <td colSpan={9}>Итого: {sortedRows.length}</td>
                          <td className="col-num payment-distribution-page__td-amount">
                            {formatContractSumDisplay(totalAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentDistributionPage;
